import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'), {ssr: false});
import parse from 'html-react-parser';
import CommonCard from '@/components/Ui-components/home/common-card';
import ScrollableTabs from '@/components/Common/ScrollableTabs';
import SectionHeader from '@/components/Common/SectionHeader';
import type {SxProps, Theme} from '@mui/material/styles';
import {colors, fontSize, overlay, spacing} from '@/styles/tokens';

// Mirrors the former TabTechnology.module.scss `.container` rule (Grid wrapper).
// !important preserved: competes with MUI's own `.MuiGrid-container` defaults.
const containerSx = {
    display: 'flex !important',
    justifyContent: 'center !important',
    gap: `${spacing[24]} !important`
} as const;

// Migrated from globals.scss `.tab-child-wrapper` (sole consumer: the Grid item below).
// The nested `.tech_label { box-shadow: none !important; }` declaration under the same
// rule is NOT migrated here — grep-verified zero elements in the codebase carry a
// `tech_label` className (CommonCard, the only child rendered inside this Grid item,
// has no such class), so that nested rule has no current DOM owner. Left in place in
// globals.scss unmigrated/inert; flagged as a blocker rather than deleted.
const tabChildWrapperSx = {
    width: '150px !important'
} as const;

// Mirrors the former nested `.container :global(.label) a` rule — the anchor can come
// from CMS-parsed HTML (html-react-parser), but `& a` still reaches it since it's a
// plain descendant selector applied to whatever renders inside this Typography.
const labelLinkSx = {
    '& a': {
        textDecoration: 'none !important',
        color: `${colors.text.primary} !important`,
        '&:hover': {textDecoration: 'underline !important'}
    }
} as const;

// Mirrors the former `.tabHeadingTechnology` + `.serviceHeadingTab` classes. ScrollableTabs
// owns the actual <button> markup, so this is handed to it via the tabSx slot instead of a
// CSS Modules classname. The former activeTabClassName="custom-heading-gradient-color" is not
// carried over: it applied the same gradients.primaryActive value that ScrollableTabs' own
// [aria-selected] rule already applies, so it was a redundant duplicate, not a distinct state.
const tabSx = {
    color: `${colors.text.primary} !important`,
    textTransform: 'capitalize !important',
    fontSize: `${fontSize[20]} !important`,
    '@media only screen and (max-width: 430px)': {
        fontSize: `${fontSize[16]} !important`
    },
    '@media screen and (max-width: 320px)': {
        fontSize: `${fontSize[15]} !important`
    }
} as const;

// `variant="dark"`: for a section on a dark band (e.g. `bg-deep-blue`). Copy turns white,
// the panel goes transparent, and the active tab swaps ScrollableTabs' dark-navy gradient
// (invisible on dark) for solid brand orange. `!important` because the active rule lives
// in ScrollableTabs.module.scss at the same specificity.
const darkTabSx = {
    ...tabSx,
    color: `${colors.text.inverse} !important`,
    // Drop the gradient-clipped-to-text treatment and colour the label directly: setting the
    // `background` shorthand here would also reset `background-clip` and fill the whole tab.
    '&[aria-selected="true"]': {
        background: 'none !important',
        color: `${colors.accent.orange} !important`,
        WebkitTextFillColor: `${colors.accent.orange} !important`
    },
    '&[aria-selected="true"]::after': {background: `${colors.accent.orange} !important`}
} as const;
const darkTextSx = {color: colors.text.inverse} as const;
// `fullWidthHeader` lifts SectionHeader's 64rem cap so a long heading can sit on one line.
// The cap is responsive (`{xs, md}`), so the override has to be too.
const fullWidthHeaderSx = {maxWidth: {xs: 'none', md: 'none'}} as const;
const lightTabsRuleSx = {borderBottom: 1, borderColor: 'divider'} as const;
const darkTabsRuleSx = {borderBottom: `1px solid ${overlay.whiteMid}`, color: colors.text.inverse} as const;
const lightPanelSx = {bgcolor: 'background.paper'} as const;
const darkPanelSx = {bgcolor: 'transparent'} as const;

// `wrapperSx` is an additive sx escape hatch for the tabs-panel outer Box, alongside the
// existing `className` string passthrough. `className` is currently unused by every
// consumer (grep-verified) — left in place rather than removed, since removing an unused
// prop is a separate, deliberate decision, not part of this API-completion pass.
export interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
export function CustomTabPanel(props: Readonly<TabPanelProps>) {
    const {children, value, index, ...other} = props;

    // Render children for ALL panels (not just the active one) so every tab's
    // content is present in the server-rendered HTML and crawlable by search
    // engines (SEO #33). Inactive panels are hidden via the `hidden` attribute
    // (display:none), so the tabbed UX is unchanged.
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            <Box sx={{pt: 3}}>{children}</Box>
        </div>
    );
}
const TabTechnology = (props: any) => {
    const {heading, subheading, technology, className, wrapperSx, iconSize, variant, fullWidthHeader, cardPadding} = props;
    const isDark = variant === 'dark';
    // Optional fixed icon box (px). Unset, icons render at their intrinsic size as before.
    // Passed as `style`, not `sx`: DisplayImage puts an inline `width/height: auto` on static
    // (CDN) images, which would beat an sx class, and merges `style` after it.
    const iconStyle = React.useMemo<React.CSSProperties | undefined>(
        () => (iconSize ? {width: iconSize, height: iconSize, objectFit: 'contain'} : undefined),
        [iconSize]
    );
    const [value, setValue] = React.useState(0);

    const renderChildren = (data: any): any => {
        return (
            <Grid container sx={containerSx}>
                {data?.map((data: any, index: number) => (
                    <Grid item className="tech-label-wrapper tab-child-wrapper" key={index + 1} sx={tabChildWrapperSx}>
                        <CommonCard padding={cardPadding}>
                            <Box textAlign="center">
                                {data?.src && (
                                    <DisplayImage
                                        src={data.src}
                                        alt={data.label || data.alt}
                                        priority={false}
                                        className="step-image"
                                        style={iconStyle}
                                    />
                                )}

                                {data?.label ? (
                                    <Box sx={{wordBreak: 'break-word'}}>
                                        <Typography sx={labelLinkSx}>{parse(data.label)} </Typography>
                                    </Box>
                                ) : (
                                    <Box></Box>
                                )}
                            </Box>
                        </CommonCard>
                    </Grid>
                ))}
            </Grid>
        );
    };
    return (
        <Box className="tab-technology-wrapper">
            <SectionHeader
                heading={heading}
                subheading={subheading ? parse(subheading) : undefined}
                subheadingClassName="remove-link-underline"
                headingSx={isDark ? darkTextSx : undefined}
                subheadingSx={isDark ? darkTextSx : undefined}
                wrapperSx={fullWidthHeader ? fullWidthHeaderSx : undefined}
            />
            <Box sx={[isDark ? darkPanelSx : lightPanelSx, wrapperSx] as SxProps<Theme>} className={className}>
                <Box sx={isDark ? darkTabsRuleSx : lightTabsRuleSx} mb={3}>
                    <ScrollableTabs
                        value={value}
                        onChange={setValue}
                        items={technology?.map((item: any) => item.header) ?? []}
                        ariaLabel={heading}
                        tabSx={isDark ? darkTabSx : tabSx}
                    />
                </Box>
                {technology?.map((item: any, index: number) => (
                    <CustomTabPanel value={value} index={index} key={index + 1}>
                        {renderChildren(item.children)}
                    </CustomTabPanel>
                ))}
            </Box>
        </Box>
    );
};

export default memo(TabTechnology);
